import { inject, Injectable, Injector } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { PublicProductDetails } from '../models/public-product-details';
import { PublicProductApiService } from '../api/services/public-product-api.service';
import { PublicProductCharacteristic } from '../models/public-product-characteristic';
import { PublicProductAttribute } from '../models/public-product-attribute';
import { multimediaFromResponse } from '@web/shared/module/multimedia';
import { executeOrNull, Nillable } from '@web/utils';
import { PublicProductCosmeticBrand } from '../models/public-product-cosmetic-brand';
import {
  CollectionFilterRequestBuilder,
  CollectionRequestBuilder,
} from '@web/common';
import { PublicProductCompositeCollectionItem } from '../models/public-product-composite-collection-item';
import { publicSalesFromResponse } from '@web/shared/module/shop';
import { PublicAvailableProduct } from '../models/public-available-product';
import { PublicProductAuthService } from './public-product-auth.service';
import { PublicProductsQuery } from '../models/public-products-query';
import { PublicProductSortType } from '../enums/public-product-sort-type';
import { PublicProductCollectionFilter } from '../models/public-product-collection-filter';
import { PublicProduct } from '../models/public-product';

@Injectable({
  providedIn: 'root',
})
export class PublicProductDataSourceService {
  private readonly productApi = inject(PublicProductApiService);
  private readonly collectionRequestBuilder = inject(CollectionRequestBuilder);
  private readonly publicProductAuth = inject(PublicProductAuthService);

  private readonly injector = inject(Injector);

  public fetchCompositeCollection(): Observable<
    PublicProductCompositeCollectionItem[]
  > {
    const request = this.collectionRequestBuilder.disablePagination().build();

    return this.productApi
      .fetchCompositeCollection({ request, cacheable: true })
      .pipe(
        map(({ items }) =>
          items.map(
            (item) =>
              new PublicProductCompositeCollectionItem({
                id: item.id,
                name: item.name,
                headline: item.headline,
                description: item.description,
                price: item.price,
                paymentPeriod: item.paymentPeriod,
                sales: publicSalesFromResponse(item.sales),
              }),
          ),
        ),
      );
  }

  public fetchDetailsBySlug(
    slug: string,
    cacheable = true,
  ): Observable<PublicProductDetails> {
    return this.publicProductAuth.fetchAuthorizedDetails(slug).pipe(
      map(
        (response) =>
          new PublicProductDetails({
            slug: response.slug,
            id: response.id,
            characteristics: response.characteristics.map(
              (characteristic) =>
                new PublicProductCharacteristic({
                  content: characteristic.value,
                  id: characteristic.id,
                  label: characteristic.label,
                }),
            ),
            sales: publicSalesFromResponse(response.sales),
            attributes: response.attributes.map(
              (attribute) =>
                new PublicProductAttribute({
                  content: attribute.value,
                  id: attribute.id,
                  label: attribute.productAttribute.label,
                }),
            ),
            cosmeticBrand: executeOrNull(
              response.cosmeticBrand,
              (brand) =>
                new PublicProductCosmeticBrand({
                  id: brand.id,
                  name: brand.name,
                  slug: brand.slug,
                  logo: multimediaFromResponse(brand.logo),
                }),
            ),

            vendorUrl: response.vendorUrl,
            name: response.name,
            files: response.files.map((file) => multimediaFromResponse(file)),
          }),
      ),
    );
  }

  public fetchAvailableProducts(
    cacheable = true,
  ): Observable<PublicAvailableProduct[]> {
    return this.productApi
      .fetchAvailableProductCollection({ cacheable: cacheable })
      .pipe(
        map((response) =>
          response.items.map(
            (item) =>
              new PublicAvailableProduct({
                name: item.name,
                slug: item.slug,
                id: item.id,
                type: item.type,
                sales: publicSalesFromResponse(item.sales),
                featuredFile: multimediaFromResponse(item.featuredFile),
              }),
          ),
        ),
        catchError(() => of([])),
      );
  }

  public fetchProducts(
    query?: Nillable<PublicProductsQuery>,
  ): Observable<PublicProduct[]> {
    const request = this.collectionRequestBuilder
      .filters(
        (
          builder: CollectionFilterRequestBuilder<PublicProductCollectionFilter>,
        ) =>
          builder
            .appendText((builder) =>
              builder.caseIntensiveLikeFor('name').value(query?.phrase).build(),
            )
            .build(),
      )
      .sorts((builder) => {
        switch (query?.sortType) {
          case PublicProductSortType.NameAscending:
            return [builder.ascendingFor('name').build()];

          case PublicProductSortType.NameDescending:
            return [builder.descendingFor('name').build()];

          case PublicProductSortType.CreatedAtAscending:
            return [builder.ascendingFor('createdAt').build()];

          case PublicProductSortType.CreatedAtDescending:
            return [builder.descendingFor('createdAt').build()];

          default:
            return [];
        }
      })
      .disablePagination()
      .build();

    return this.productApi.fetchProductsCollection({ request }).pipe(
      map((response) =>
        response.items.map(
          (item) =>
            new PublicProduct({
              id: item.id,
              name: item.name,
              slug: item.slug,
              type: item.type,
              featuredFile: multimediaFromResponse(item.featuredFile),
              sales: publicSalesFromResponse(item.sales),
            }),
        ),
      ),
      catchError(() => of([])),
    );
  }
}
